// NotificationProvider — single-toast-slot dispatcher for authenticated
// surfaces. The plan calls the architecture piece "Notification" (since the
// Provider could grow into multi-shape dispatch — banners, alerts) and the
// hook `useToast` because Toast is what the design system ships today.
//
// Contract:
//  - `toast(opts)` dispatches a notification and returns a handle.
//  - At most ONE toast is visible. Dispatching while one is open dismisses
//    the prior with reason='supplant' (single-pending invariant).
//  - `handle.dismiss()` programmatically closes with reason='manual'.
//  - When Radix closes via its own machinery (timeout / Esc / swipe), the
//    Provider defaults the reason to 'timeout'. Radix conflates timer-elapse
//    and Esc into the same close path; consumers that need to distinguish
//    can layer their own keyboard listener on top.
//  - `handle.update(partial)` mutates the currently-open toast in place.

import * as RadixToast from '@radix-ui/react-toast';
import {
	createContext,
	type FC,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
} from 'react';

import { Toast } from './toast';

type NotificationDismissReason = 'timeout' | 'manual' | 'esc' | 'supplant';

interface NotificationOptions {
	variant: 'success' | 'error';
	title: ReactNode;
	action?: ReactNode;
	duration_ms?: number;
	on_dismiss?: (reason: NotificationDismissReason) => void;
}

interface NotificationHandle {
	id: string;
	dismiss(): void;
	update(opts: Partial<NotificationOptions>): void;
}

interface NotificationContextValue {
	toast: (opts: NotificationOptions) => NotificationHandle;
}

const NotificationContext = createContext<NotificationContextValue | null>(
	null,
);

interface ActiveToast {
	id: string;
	options: NotificationOptions;
}

const DEFAULT_DURATION_MS = 5000;

const NotificationProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const [active, set_active] = useState<ActiveToast | null>(null);
	// Tracks why the current toast is closing so the Radix onOpenChange(false)
	// path can route to the right on_dismiss reason. Defaults to 'timeout'
	// because timer-elapse / Esc are Radix's own close paths and arrive
	// through the same callback with no reason attached.
	const dismiss_reason = useRef<NotificationDismissReason>('timeout');

	const toast = useCallback<NotificationContextValue['toast']>((opts) => {
		const new_id = crypto.randomUUID();
		set_active((prior) => {
			if (prior) {
				prior.options.on_dismiss?.('supplant');
			}
			dismiss_reason.current = 'timeout';
			return { id: new_id, options: opts };
		});
		return {
			id: new_id,
			dismiss() {
				set_active((curr) => {
					if (curr?.id !== new_id) return curr;
					dismiss_reason.current = 'manual';
					curr.options.on_dismiss?.('manual');
					return null;
				});
			},
			update(partial) {
				set_active((curr) => {
					if (curr?.id !== new_id) return curr;
					return {
						...curr,
						options: { ...curr.options, ...partial },
					};
				});
			},
		};
	}, []);

	// Routes Radix-originated close (timer elapse / Esc / swipe) to the active
	// toast's on_dismiss. Manual dismissals call on_dismiss('manual') inside
	// handle.dismiss() and clear active synchronously, so the Toast is gone
	// from the tree before Radix's onOpenChange could re-enter — no double-fire
	// guard needed.
	const handle_open_change = useCallback((open: boolean) => {
		/* v8 ignore next — Radix only fires onOpenChange(false) for our controlled
		   open prop; the open=true branch is defensive against future API drift. */
		if (open) return;
		set_active((curr) => {
			/* v8 ignore next — only callable while a Toast is mounted; defensive */
			if (!curr) return null;
			const callback = curr.options.on_dismiss;
			if (callback) callback(dismiss_reason.current);
			return null;
		});
	}, []);

	const value = useMemo<NotificationContextValue>(() => ({ toast }), [toast]);

	return (
		<NotificationContext.Provider value={value}>
			<RadixToast.Provider swipeDirection='right'>
				{children}
				{active ? (
					// `key={active.id}` forces a fresh React mount on every
					// supplant so Radix Toast's internal auto-dismiss timer
					// restarts from zero. Without the key the same Toast
					// instance persists across supplants and inherits the
					// remaining time from the prior toast — the new toast
					// would vanish ~(5s minus elapsed) later instead of
					// after a full 5s.
					<Toast
						key={active.id}
						variant={active.options.variant}
						open={true}
						on_open_change={handle_open_change}
						title={active.options.title}
						action={active.options.action}
						duration_ms={active.options.duration_ms ?? DEFAULT_DURATION_MS}
					/>
				) : null}
				<RadixToast.Viewport className='toast-viewport' />
			</RadixToast.Provider>
		</NotificationContext.Provider>
	);
};

function useToast(): NotificationContextValue {
	const ctx = useContext(NotificationContext);
	if (!ctx) {
		throw new Error('useToast must be used inside a <NotificationProvider>.');
	}
	return ctx;
}

export type {
	NotificationDismissReason,
	NotificationHandle,
	NotificationOptions,
};
export { NotificationProvider, useToast };
